/* ==========================================================================
   EVIGIEE — product page behaviour
   <media-gallery>, <variant-picker>, <product-form>
   ========================================================================== */

(function () {
  'use strict';

  const $ = (sel, root) => (root || document).querySelector(sel);
  const $$ = (sel, root) => Array.from((root || document).querySelectorAll(sel));
  const strings = (window.theme && window.theme.strings) || {};

  /* =========================================================
     Media gallery
     ========================================================= */

  class MediaGallery extends HTMLElement {
    connectedCallback() {
      this.viewport = $('[data-viewport]', this);
      this.slides = $$('[data-slide]', this);
      this.thumbs = $$('[data-thumb]', this);
      this.dots = $$('[data-dot]', this);
      this.stacked = this.classList.contains('gallery--stacked');
      this.index = Math.max(
        0,
        this.slides.findIndex((s) => !s.hidden)
      );

      this.thumbs.forEach((thumb) =>
        thumb.addEventListener('click', () => this.select(Number(thumb.dataset.index)))
      );
      this.dots.forEach((dot) =>
        dot.addEventListener('click', () => this.select(Number(dot.dataset.index)))
      );

      const prev = $('[data-gallery-prev]', this);
      const next = $('[data-gallery-next]', this);
      if (prev) prev.addEventListener('click', () => this.select(this.index - 1));
      if (next) next.addEventListener('click', () => this.select(this.index + 1));

      this.addEventListener('keydown', (e) => {
        if (e.target.closest('[data-thumb], [data-dot]')) {
          if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
            e.preventDefault();
            this.select(this.index + 1, true);
          }
          if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
            e.preventDefault();
            this.select(this.index - 1, true);
          }
        }
      });

      this.initSwipe();
      this.initZoom();
      this.initLightbox();
      this.update();
    }

    /* Mobile: the viewport is a native scroll-snap carousel. All slides are
       visible there, so unhide them below 900px and follow scroll position. */
    initSwipe() {
      if (!this.viewport) return;

      this.mql = window.matchMedia('(max-width: 900px)');
      const apply = () => {
        if (this.mql.matches || this.stacked) {
          this.slides.forEach((s) => {
            s.hidden = false;
          });
          if (this.mql.matches) this.scrollToIndex(this.index, 'auto');
        } else {
          this.slides.forEach((s, i) => {
            s.hidden = i !== this.index;
          });
        }
      };
      apply();
      this.mql.addEventListener('change', apply);

      let raf;
      this.viewport.addEventListener(
        'scroll',
        () => {
          if (!this.mql.matches) return;
          cancelAnimationFrame(raf);
          raf = requestAnimationFrame(() => {
            const i = Math.round(this.viewport.scrollLeft / this.viewport.clientWidth);
            if (i !== this.index && this.slides[i]) {
              this.index = i;
              this.update(false);
            }
          });
        },
        { passive: true }
      );
    }

    scrollToIndex(index, behavior) {
      if (!this.viewport) return;
      this.viewport.scrollTo({
        left: index * this.viewport.clientWidth,
        behavior: behavior || 'smooth'
      });
    }

    select(index, keepFocus) {
      if (index < 0) index = 0;
      if (index > this.slides.length - 1) index = this.slides.length - 1;
      this.index = index;

      if (this.mql && this.mql.matches) {
        this.scrollToIndex(index);
      } else if (!this.stacked) {
        this.slides.forEach((s, i) => {
          s.hidden = i !== index;
        });
        this.pauseMedia();
      } else {
        const slide = this.slides[index];
        if (slide) slide.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
      }

      this.update();
      if (keepFocus && this.thumbs[index]) this.thumbs[index].focus();
    }

    selectByMediaId(mediaId) {
      const idx = this.slides.findIndex((s) => s.dataset.mediaId === String(mediaId));
      if (idx > -1) this.select(idx);
    }

    pauseMedia() {
      $$('video', this).forEach((v) => v.pause());
      $$('iframe', this).forEach((frame) => {
        try {
          frame.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
        } catch (err) {
          /* cross origin */
        }
      });
    }

    update(scrollThumb) {
      this.thumbs.forEach((t, i) => t.setAttribute('aria-current', String(i === this.index)));
      this.dots.forEach((d, i) => d.setAttribute('aria-current', String(i === this.index)));

      const prev = $('[data-gallery-prev]', this);
      const next = $('[data-gallery-next]', this);
      if (prev) prev.disabled = this.index === 0;
      if (next) next.disabled = this.index === this.slides.length - 1;

      if (scrollThumb !== false && this.thumbs[this.index]) {
        const thumb = this.thumbs[this.index];
        const box = thumb.parentElement;
        if (box && box.scrollHeight > box.clientHeight) {
          thumb.scrollIntoView({ block: 'nearest' });
        }
      }
    }

    /* Hover-pan zoom on the active image */
    initZoom() {
      if (this.dataset.zoom !== 'true') return;
      $$('.gallery__slide--zoom-hover', this).forEach((slide) => {
        slide.addEventListener('mousemove', (e) => {
          const rect = slide.getBoundingClientRect();
          const x = ((e.clientX - rect.left) / rect.width) * 100;
          const y = ((e.clientY - rect.top) / rect.height) * 100;
          slide.style.setProperty('--zoom-x', x + '%');
          slide.style.setProperty('--zoom-y', y + '%');
        });
      });
    }

    initLightbox() {
      this.lightbox = $('[data-lightbox]', this);
      if (!this.lightbox) return;

      const body = $('[data-lightbox-body]', this.lightbox);
      const open = (startIndex) => {
        body.innerHTML = '';
        this.slides.forEach((slide) => {
          const img = $('img', slide);
          if (!img) return;
          const clone = document.createElement('img');
          clone.src = img.currentSrc || img.src;
          clone.srcset = img.srcset || '';
          clone.sizes = '(min-width: 1100px) 1100px, 100vw';
          clone.alt = img.alt;
          clone.loading = 'lazy';
          body.appendChild(clone);
        });
        this.lightbox.hidden = false;
        document.body.classList.add('overlay-open');
        this._opener = document.activeElement;
        $('[data-close-lightbox]', this.lightbox).focus();
        const target = body.children[startIndex];
        if (target) target.scrollIntoView({ block: 'start' });
      };

      const close = () => {
        this.lightbox.hidden = true;
        document.body.classList.remove('overlay-open');
        if (this._opener) this._opener.focus();
      };

      const openBtn = $('[data-open-lightbox]', this);
      if (openBtn) openBtn.addEventListener('click', () => open(this.index));

      if (this.dataset.zoom === 'true') {
        $$('.gallery__slide--zoomable img', this).forEach((img, i) => {
          if (this.closest('media-gallery').querySelector('.gallery__slide--zoom-hover')) return;
          img.addEventListener('click', () => open(i));
        });
      }

      $('[data-close-lightbox]', this.lightbox).addEventListener('click', close);
      this.lightbox.addEventListener('click', (e) => {
        if (e.target === this.lightbox) close();
      });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && !this.lightbox.hidden) close();
      });
    }
  }

  if (!customElements.get('media-gallery')) customElements.define('media-gallery', MediaGallery);

  /* =========================================================
     Variant picker
     ========================================================= */

  class VariantPicker extends HTMLElement {
    connectedCallback() {
      const dataEl = $('[data-variant-data]', this);
      if (!dataEl) return;

      try {
        this.variants = JSON.parse(dataEl.textContent);
      } catch (err) {
        this.variants = [];
        return;
      }

      this.root = this.closest('[data-product-root]') || document;
      this.section = this.dataset.section;

      this.addEventListener('change', () => this.onChange());
      this.updateAvailability();
    }

    get selectedOptions() {
      const selects = $$('[data-option-select]', this);
      if (selects.length) return selects.map((s) => s.value);

      const groups = {};
      $$('[data-option-input]:checked', this).forEach((input) => {
        groups[input.dataset.optionPosition] = input.value;
      });
      return Object.keys(groups)
        .sort((a, b) => Number(a) - Number(b))
        .map((k) => groups[k]);
    }

    findVariant(options) {
      return this.variants.find((v) => v.options.every((opt, i) => opt === options[i]));
    }

    onChange() {
      const options = this.selectedOptions;
      const variant = this.findVariant(options);

      this.updateLabels();
      this.updateAvailability();
      this.updateVariantState(variant);
    }

    updateLabels() {
      $$('.variant-picker__option', this).forEach((group) => {
        const label = $('[data-option-value-label]', group);
        const checked = $('[data-option-input]:checked', group);
        if (label && checked) label.textContent = '· ' + checked.value;
      });
    }

    /* Grey out option values that produce no purchasable variant given the
       other currently selected options. */
    updateAvailability() {
      const current = this.selectedOptions;

      $$('.variant-picker__option', this).forEach((group, groupIndex) => {
        $$('[data-option-input]', group).forEach((input) => {
          const trial = current.slice();
          trial[groupIndex] = input.value;
          const match = this.variants.find((v) =>
            v.options.every((opt, i) => (i === groupIndex ? opt === input.value : opt === trial[i]))
          );
          const label = this.querySelector(`label[for="${input.id}"]`);
          if (!label) return;
          const unavailable = !match || !match.available;
          label.classList.toggle('is-unavailable', unavailable);
          input.setAttribute('aria-disabled', String(unavailable));
        });

        $$('[data-option-select] option', group).forEach((option) => {
          const trial = current.slice();
          trial[groupIndex] = option.value;
          const match = this.variants.find((v) => v.options.every((opt, i) => opt === trial[i]));
          option.textContent = option.value + (match && match.available ? '' : ` — ${strings.soldOut}`);
        });
      });
    }

    updateVariantState(variant) {
      const root = this.root;

      // hidden id input
      const idInput = $('[data-variant-id]', root);
      if (idInput) {
        idInput.value = variant ? variant.id : '';
        idInput.disabled = !variant;
      }

      // price
      const priceWrapper = $('[data-price-wrapper]', root);
      if (priceWrapper && variant) {
        const priceEl = $('[data-price]', priceWrapper);
        const compareEl = $('[data-compare-price]', priceWrapper);
        const badgeEl = $('[data-discount-badge]', priceWrapper);
        const unitEl = $('[data-unit-price]', priceWrapper);

        if (priceEl) priceEl.innerHTML = variant.price_html;

        const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
        if (compareEl) {
          compareEl.innerHTML = onSale ? variant.compare_html : '';
          compareEl.hidden = !onSale;
        }
        if (badgeEl) {
          badgeEl.hidden = !onSale;
          if (onSale) {
            const pct = Math.round(
              ((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100
            );
            if (badgeEl.textContent.indexOf('%') > -1 || /\d/.test(badgeEl.textContent)) {
              badgeEl.textContent = badgeEl.textContent.replace(/\d+%?/, pct + '%');
            }
          }
        }
        if (unitEl) {
          unitEl.hidden = !variant.unit_price_html;
          if (variant.unit_price_html) unitEl.textContent = variant.unit_price_html;
        }
      }

      // sku
      const sku = $('[data-sku]', root);
      if (sku) sku.textContent = (variant && variant.sku) || '—';

      // inventory
      const inventory = $('[data-inventory]', root);
      if (inventory && variant) {
        const text = $('[data-inventory-text]', inventory);
        const threshold = Number(inventory.dataset.lowThreshold || 10);
        const tracked = variant.inventory_management === 'shopify';
        let label = strings.soldOut;
        let low = false;
        if (variant.available) {
          if (tracked && variant.inventory_quantity > 0 && variant.inventory_quantity <= threshold) {
            label = `Only ${variant.inventory_quantity} left`;
            low = true;
          } else {
            label = 'In stock';
          }
        }
        if (text) text.textContent = label;
        inventory.classList.toggle('product__inventory--low', low);
      }

      // quantity max
      const qtyInput = $('[data-qty-input]', root);
      if (qtyInput && variant) {
        if (variant.inventory_management === 'shopify' && variant.inventory_policy === 'deny') {
          qtyInput.max = variant.inventory_quantity;
        } else {
          qtyInput.removeAttribute('max');
        }
        const stepper = qtyInput.closest('quantity-input');
        if (stepper && stepper.validate) stepper.validate();
      }

      // add to cart button
      const button = $('[data-add-to-cart]', root);
      const buttonText = $('[data-add-to-cart-text]', root);
      if (button && buttonText) {
        if (!variant) {
          button.disabled = true;
          buttonText.textContent = strings.unavailable;
        } else if (!variant.available) {
          button.disabled = true;
          buttonText.textContent = strings.soldOut;
        } else {
          button.disabled = false;
          const base = button.dataset.baseLabel || strings.addToCart;
          const priceSpan = $('[data-atc-price]', button);
          if (priceSpan) {
            buttonText.innerHTML = `${base} · <span data-atc-price>${variant.price_html}</span>`;
          } else {
            buttonText.textContent = base;
          }
        }
      }

      // sticky bar
      const stickyPrice = $('[data-sticky-price]');
      if (stickyPrice && variant) stickyPrice.innerHTML = variant.price_html;

      // media
      if (variant && variant.media_id) {
        const gallery = $('media-gallery', root);
        if (gallery && gallery.selectByMediaId) gallery.selectByMediaId(variant.media_id);
      }

      // url + history
      if (variant && this.dataset.updateUrl !== 'false') {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }

      document.dispatchEvent(new CustomEvent('variant:changed', { detail: { variant } }));
    }
  }

  if (!customElements.get('variant-picker')) customElements.define('variant-picker', VariantPicker);

  /* =========================================================
     Product form
     ========================================================= */

  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = $('form', this);
      if (!this.form) return;

      this.button = $('[data-add-to-cart]', this);
      this.errorBox = $('[data-form-error]', this);
      this.errorText = $('[data-form-error-text]', this);

      const label = $('[data-add-to-cart-text]', this);
      if (this.button && label) {
        this.button.dataset.baseLabel = label.textContent.split('·')[0].trim();
      }

      this.form.addEventListener('submit', (e) => this.onSubmit(e));
    }

    showError(message) {
      if (!this.errorBox) return;
      this.errorText.textContent = message;
      this.errorBox.classList.add('is-visible');
    }

    clearError() {
      if (this.errorBox) this.errorBox.classList.remove('is-visible');
    }

    async onSubmit(event) {
      event.preventDefault();
      if (!this.button || this.button.getAttribute('aria-busy') === 'true') return;

      this.clearError();

      const idInput = $('[data-variant-id]', this);
      if (!idInput || !idInput.value) {
        this.showError(strings.selectOption || 'Please choose an option');
        return;
      }

      this.button.setAttribute('aria-busy', 'true');
      this.button.disabled = true;

      try {
        const formData = new FormData(this.form);
        await window.themeCart.add(formData);

        if ((window.theme && window.theme.cartType) === 'drawer') {
          window.themeCart.open();
        } else {
          window.themeUtils.toast(strings.added || 'Added to cart');
        }
      } catch (err) {
        this.showError(err.message || strings.error);
      } finally {
        this.button.removeAttribute('aria-busy');
        this.button.disabled = false;
      }
    }
  }

  if (!customElements.get('product-form')) customElements.define('product-form', ProductForm);

  /* =========================================================
     Sticky mobile add-to-cart
     ========================================================= */

  function initStickyAtc() {
    const bar = $('[data-sticky-atc]');
    const button = $('[data-add-to-cart]');
    if (!bar || !button) return;

    bar.classList.add('sticky-atc--enabled');
    $('[data-sticky-atc-button]', bar).addEventListener('click', () => {
      button.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setTimeout(() => button.click(), 320);
    });

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => bar.classList.toggle('is-visible', !entry.isIntersecting));
      },
      { rootMargin: '-80px 0px 0px 0px' }
    );
    observer.observe(button);
  }

  /* =========================================================
     Product recommendations + recently viewed
     ========================================================= */

  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      const url = this.dataset.url;
      if (!url) return;

      const load = () => {
        fetch(url)
          .then((res) => res.text())
          .then((text) => {
            const parsed = new DOMParser().parseFromString(text, 'text/html');
            const incoming = parsed.querySelector('product-recommendations');
            if (incoming && incoming.innerHTML.trim().length) {
              this.innerHTML = incoming.innerHTML;
            } else {
              this.closest('[data-recs-section]')?.remove();
            }
          })
          .catch(() => this.closest('[data-recs-section]')?.remove());
      };

      if ('IntersectionObserver' in window) {
        const io = new IntersectionObserver(
          (entries) => {
            if (entries[0].isIntersecting) {
              io.disconnect();
              load();
            }
          },
          { rootMargin: '400px' }
        );
        io.observe(this);
      } else {
        load();
      }
    }
  }

  if (!customElements.get('product-recommendations')) {
    customElements.define('product-recommendations', ProductRecommendations);
  }

  class RecentlyViewed extends HTMLElement {
    connectedCallback() {
      let handles = [];
      try {
        handles = JSON.parse(localStorage.getItem('evigiee:recently-viewed') || '[]');
      } catch (err) {
        handles = [];
      }

      const current = document.body.dataset.productHandle;
      handles = handles.filter((h) => h && h !== current).slice(0, Number(this.dataset.limit || 4));

      if (!handles.length) {
        this.closest('[data-recently-viewed-section]')?.remove();
        return;
      }

      const query = handles.map((h) => `handle:${h}`).join(' OR ');
      const url = `${this.dataset.searchUrl}?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=${handles.length}&section_id=${this.dataset.section}`;

      fetch(url)
        .then((res) => res.text())
        .then((text) => {
          const parsed = new DOMParser().parseFromString(text, 'text/html');
          const incoming = parsed.querySelector('recently-viewed');
          if (incoming && incoming.innerHTML.trim().length) {
            this.innerHTML = incoming.innerHTML;
          } else {
            this.closest('[data-recently-viewed-section]')?.remove();
          }
        })
        .catch(() => this.closest('[data-recently-viewed-section]')?.remove());
    }
  }

  if (!customElements.get('recently-viewed')) customElements.define('recently-viewed', RecentlyViewed);

  /* ---------- boot ---------- */

  function init() {
    initStickyAtc();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
})();
